import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Circle, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box,
  Typography,
  Button,
  Chip,
  Skeleton,
  Alert,
  Paper,
} from '@mui/material';
import { geofenceApi } from '../../services/api';

const IOS_STORE_URL = 'https://apps.apple.com/app/skyradar/id000000000'; // replace with real App Store ID
const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=org.skyradar.skyradar';

const LIGHTNING_RECENT = '/images/map/lightning_5min.png';
const LIGHTNING_MEDIUM = '/images/map/lightning_30min.png';
const LIGHTNING_OLD = '/images/map/lightning_old.png';

const ICONS = {
  recent: new L.Icon({ iconUrl: LIGHTNING_RECENT, iconSize: [24, 24], iconAnchor: [12, 12] }),
  medium: new L.Icon({ iconUrl: LIGHTNING_MEDIUM, iconSize: [24, 24], iconAnchor: [12, 12] }),
  old: new L.Icon({ iconUrl: LIGHTNING_OLD, iconSize: [24, 24], iconAnchor: [12, 12] }),
};

function getMarkerIcon(timestamp) {
  const ageMinutes = (Date.now() - new Date(timestamp)) / 60000;
  if (ageMinutes <= 5) return ICONS.recent;
  if (ageMinutes <= 30) return ICONS.medium;
  return ICONS.old;
}

export function radiusToZoom(lat, radiusM) {
  const z = Math.log2(
    (156543.03392 * Math.cos(lat * Math.PI / 180) * 100) / radiusM
  );
  return Math.max(5, Math.min(15, Math.round(z)));
}

function isMobileDevice() {
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) || window.innerWidth < 768;
}

function handleOpenApp(code) {
  if (!isMobileDevice()) return;

  window.location.href = `skyradar://geofence?code=${code}`;

  const timer = setTimeout(() => {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) {
      window.location.href = IOS_STORE_URL;
    } else {
      window.location.href = ANDROID_STORE_URL;
    }
  }, 600);

  const cleanup = () => {
    if (document.hidden) {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', cleanup);
    }
  };
  document.addEventListener('visibilitychange', cleanup);
}

export default function GeofencePage() {
  const { code } = useParams();
  const [geofence, setGeofence] = useState(null);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('loading'); // 'loading' | 'error' | 'notfound' | 'loaded'
  const [mobile] = useState(() => isMobileDevice());

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const gRes = await geofenceApi.getByCode(code);
        if (cancelled) return;
        const g = gRes.data;
        setGeofence(g);

        try {
          const eRes = await geofenceApi.getEventsInZone(g.latitude, g.longitude, g.radius_m);
          if (!cancelled) setEvents(eRes.data?.events || []);
        } catch (_) {
          // Events are best-effort; geofence data is sufficient to render
        }

        if (!cancelled) setStatus('loaded');
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setStatus('notfound');
        } else {
          setStatus('error');
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [code]);

  // Dynamic OG / title meta tags
  useEffect(() => {
    if (!geofence) return;
    const name = geofence.name || 'Geofence';
    const radiusKm = Math.round(geofence.radius_m / 1000);
    const title = `${name} — SkyRadar`;
    const desc = `${radiusKm} km geofence · ${events.length} strikes in 24h · Track lightning in real time`;

    document.title = title;

    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', desc);
    setMeta('name', 'description', desc);

    return () => {
      document.title = 'SkyRadar';
      ['og:title', 'og:description'].forEach((key) => {
        const el = document.querySelector(`meta[property="${key}"]`);
        if (el) el.setAttribute('content', '');
      });
      const descEl = document.querySelector('meta[name="description"]');
      if (descEl) descEl.setAttribute('content', '');
    };
  }, [geofence, events.length]);

  if (status === 'loading') {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 2 }}>
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton width="60%" height={32} sx={{ mb: 1 }} />
        <Skeleton width="40%" height={24} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  if (status === 'notfound') {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 4, textAlign: 'center' }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This geofence link is no longer valid.
        </Alert>
        <Button variant="outlined" href="/">Visit SkyRadar</Button>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load geofence. Check your connection.
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  const name = geofence.name || 'Geofence';
  const radiusKm = Math.round(geofence.radius_m / 1000);
  const zoom = radiusToZoom(geofence.latitude, geofence.radius_m);
  const center = [geofence.latitude, geofence.longitude];
  const color = geofence.color || '#1273D1';

  return (
    <Box sx={{ position: 'relative', width: '100%', height: { xs: 'calc(100svh - 56px)', sm: 'calc(100svh - 64px)' } }}>
      {/* Full-screen map */}
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ width: '100%', height: '100%' }}
        dragging={!mobile}
        touchZoom={!mobile}
        doubleClickZoom={false}
        scrollWheelZoom={!mobile}
        zoomControl={!mobile}
        attributionControl={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap contributors'
        />
        <Circle
          center={center}
          radius={geofence.radius_m}
          pathOptions={{ color, fillColor: color, fillOpacity: 0.15, weight: 2 }}
        />
        {events.map((ev) => (
          <Marker
            key={ev.id}
            position={[ev.latitude, ev.longitude]}
            icon={getMarkerIcon(ev.timestamp)}
          />
        ))}
      </MapContainer>

      {/* Overlay info card */}
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          bottom: mobile ? 0 : 16,
          left: mobile ? 0 : 16,
          right: mobile ? 0 : 'auto',
          width: mobile ? '100%' : 300,
          borderRadius: mobile ? '12px 12px 0 0' : '12px',
          p: 2,
          zIndex: 1000,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="h6" fontWeight={700}>{name}</Typography>
          <Chip
            label={geofence.is_active ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              backgroundColor: geofence.is_active ? '#e8f5e9' : '#f5f5f5',
              color: geofence.is_active ? '#2e7d32' : '#757575',
              fontWeight: 600,
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Radius: {radiusKm} km
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {events.length} strike{events.length !== 1 ? 's' : ''} in the last 24h
        </Typography>
        {mobile ? (
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => handleOpenApp(code)}
            sx={{ borderRadius: 2, py: 1.5, fontWeight: 700 }}
          >
            Open in SkyRadar App
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              href={IOS_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ flex: 1 }}
            >
              App Store
            </Button>
            <Button
              variant="outlined"
              href={ANDROID_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ flex: 1 }}
            >
              Google Play
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
